import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { DateTime } from 'luxon';
import { TimeLogsByProjectWithLeaveRequestsDto } from '../../commonTypes/acs/timelog.types';
import { restoreAll } from '../restoreAll';
import { PatchTimelogDto, TimeLogDto } from '../../commonTypes/timelog.types';
import { ProjectStatus } from '@cyberlabrs/helpers';
import { isNotPrivileged } from '../../api/utils/roleUtils';

export interface TimeTrackingState {
    value: {
        timeTrackingTimestamp: string;
        timeTrackingWeekStart: string;
        timeTrackingWeenEnd: string;
        timeLogsByProjectWithLeaveRequests: TimeLogsByProjectWithLeaveRequestsDto;
        timeTrackingView?: TimeTrackingView;
        pendingTimeLogs: TimeLogDto[];
        openedProjectWrappers: string[];
        actingUserSub: string | null;
        monthlyLogsCache: Record<string, TimeLogsByProjectWithLeaveRequestsDto>;
    };
}

export type TimeTrackingView = 'day' | 'week' | 'month';
const SELF_CACHE_KEY = '__self__';

interface WeekAction {
    start: string;
    end: string;
}

export interface TimeStampAction {
    day: string;
    start: string;
    end: string;
}

export interface TimeLogToEdit extends PatchTimelogDto {
    id: string;
    projectId: string;
}

export const toTimeStampAction = (day: DateTime) => {
    return {
        day: day.toISO(),
        start: day.startOf('week').toISO(),
        end: day.endOf('week').toISO(),
    };
};

const initialState: TimeTrackingState = {
    value: {
        timeTrackingTimestamp: '1970-01-01T00:00:00.000Z',
        timeTrackingWeekStart: '1970-01-01T00:00:00.000Z',
        timeTrackingWeenEnd: '1970-01-01T23:59:59.999Z',
        timeLogsByProjectWithLeaveRequests: {
            leaveRequests: [],
            timeLogsByProject: [],
        },
        pendingTimeLogs: [],
        openedProjectWrappers: [],
        actingUserSub: null,
        monthlyLogsCache: {},
    },
};

const toMonthKey = (dateValue?: string | Date | null) => {
    if (!dateValue) return null;
    const parsed =
        typeof dateValue === 'string'
            ? DateTime.fromISO(dateValue)
            : dateValue instanceof Date
            ? DateTime.fromJSDate(dateValue)
            : DateTime.invalid('invalid date');
    if (!parsed.isValid) return null;
    return parsed.toFormat('yyyy-MM');
};

const invalidateMonthlyCache = (cache: Record<string, TimeLogsByProjectWithLeaveRequestsDto>, months: string[], userSub?: string | null) => {
    const monthSet = new Set(months.filter(Boolean));
    if (!monthSet.size) return;

    const prefixes = userSub === undefined ? null : [userSub || SELF_CACHE_KEY];
    for (const cacheKey of Object.keys(cache)) {
        const [cacheUserSub, cacheMonth] = cacheKey.split('|');
        if (!monthSet.has(cacheMonth)) continue;
        if (prefixes && !prefixes.includes(cacheUserSub)) continue;
        delete cache[cacheKey];
    }
};

const timeTrackingSlice = createSlice({
    name: 'counter',
    initialState,
    extraReducers: builder => builder.addCase(restoreAll, () => initialState),
    reducers: {
        setTimeTrackingTimestamp: (state, action: PayloadAction<TimeStampAction>) => {
            state.value.timeTrackingTimestamp = action.payload.day;
            state.value.timeTrackingWeekStart = action.payload.start;
            state.value.timeTrackingWeenEnd = action.payload.end;
        },
        setTimeTrackingWeek: (state, action: PayloadAction<WeekAction>) => {
            state.value.timeTrackingWeekStart = action.payload.start;
            state.value.timeTrackingWeenEnd = action.payload.end;
            state.value.timeTrackingTimestamp = action.payload.start;
        },
        setTimeLogsByProjectWithLeaveRequests: (state, action: PayloadAction<TimeLogsByProjectWithLeaveRequestsDto>) => {
            state.value.timeLogsByProjectWithLeaveRequests = action.payload;

            if (state.value.pendingTimeLogs.length > 0) {
                for (const prj of action.payload.timeLogsByProject) {
                    for (const tl of prj.timeLogs) {
                        state.value.pendingTimeLogs = state.value.pendingTimeLogs.filter(temp => temp.id !== tl.id);
                    }
                }
            }
        },
        setMonthlyLogsCacheEntry: (state, action: PayloadAction<{ key: string; value: TimeLogsByProjectWithLeaveRequestsDto }>) => {
            state.value.monthlyLogsCache[action.payload.key] = action.payload.value;
        },
        addTimeLogOfProjectForView: (state, action: PayloadAction<TimeLogDto>) => {
            const projectId = action.payload.projectId;
            const monthKey = toMonthKey(action.payload.logStarted);
            if (monthKey) {
                invalidateMonthlyCache(state.value.monthlyLogsCache, [monthKey], action.payload.userSub);
            }

            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            if (timeLogsByProject) {
                const { timeTrackingWeekStart, timeTrackingWeenEnd, timeTrackingTimestamp } = state.value;
                const { logStarted, id } = action.payload;

                const timeLogExist = timeLogsByProject.timeLogs.find(tl => tl.id === id);

                if (state.value.timeTrackingView === 'day') {
                    const isOnDay = DateTime.fromISO(timeTrackingTimestamp).hasSame(DateTime.fromISO(logStarted), 'day');
                    if (isOnDay && !timeLogExist) {
                        timeLogsByProject.timeLogs.push(action.payload);
                        state.value.pendingTimeLogs = state.value.pendingTimeLogs.filter(tl => tl.id !== action.payload.id);
                    }
                } else if (state.value.timeTrackingView === 'week') {
                    const isInWeek =
                        DateTime.fromISO(timeTrackingWeekStart) <= DateTime.fromISO(logStarted) &&
                        DateTime.fromISO(logStarted) <= DateTime.fromISO(timeTrackingWeenEnd);
                    if (isInWeek && !timeLogExist) timeLogsByProject.timeLogs.push(action.payload);
                } else if (state.value.timeTrackingView === 'month') {
                    const selectedDate = DateTime.fromISO(timeTrackingTimestamp);
                    const isInMonth = DateTime.fromISO(logStarted).hasSame(selectedDate, 'month');
                    if (isInMonth && !timeLogExist) timeLogsByProject.timeLogs.push(action.payload);
                }
            }
        },
        addTimeLogForProject: (state, action: PayloadAction<TimeLogDto>) => {
            const projectId = action.payload.projectId;
            const monthKey = toMonthKey(action.payload.logStarted);
            if (monthKey) {
                invalidateMonthlyCache(state.value.monthlyLogsCache, [monthKey], action.payload.userSub);
            }

            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            if (timeLogsByProject) {
                const timeLogExist = timeLogsByProject.timeLogs.find(tl => tl.id === action.payload.id);

                if (!timeLogExist) {
                    timeLogsByProject.timeLogs.push(action.payload);
                    state.value.pendingTimeLogs = state.value.pendingTimeLogs.filter(tl => tl.id !== action.payload.id);
                }
            }
        },
        removeTimeLogOfProject: (state, action: PayloadAction<TimeLogDto>) => {
            const timeLogId = action.payload.id;
            const projectId = action.payload.projectId;
            const monthKey = toMonthKey(action.payload.logStarted);
            if (monthKey) {
                invalidateMonthlyCache(state.value.monthlyLogsCache, [monthKey], action.payload.userSub);
            }
            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            if (timeLogsByProject) {
                timeLogsByProject.timeLogs = timeLogsByProject.timeLogs.filter(timeLog => timeLog.id !== timeLogId);
            }
        },
        addSubmissionIntervalOfProject: (state, action) => {
            const projectId = action.payload.projectId;
            const startMonth = toMonthKey(action.payload.startDate);
            const endMonth = toMonthKey(action.payload.endDate);
            const cacheUserSub = state.value.actingUserSub || null;
            invalidateMonthlyCache(state.value.monthlyLogsCache, [startMonth || '', endMonth || ''], cacheUserSub);
            const timeLogsByProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsByProjects.find(timeLogsByProject => timeLogsByProject.projectId === projectId);
            timeLogsByProject?.submissionIntervals.push(action.payload);
        },
        deleteSubmissionIntervalOfProject: (state, action) => {
            const { id, projectId } = action.payload;
            const startMonth = toMonthKey(action.payload.startDate);
            const endMonth = toMonthKey(action.payload.endDate);
            if (startMonth || endMonth) {
                const cacheUserSub = state.value.actingUserSub || null;
                invalidateMonthlyCache(state.value.monthlyLogsCache, [startMonth || '', endMonth || ''], cacheUserSub);
            } else {
                state.value.monthlyLogsCache = {};
            }
            const timeLogsByProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;

            const timeLogsByProject = timeLogsByProjects.find(timeLogsByProject => timeLogsByProject.projectId === projectId);
            if (!timeLogsByProject) return;

            const idx = timeLogsByProject.submissionIntervals.findIndex(si => si.id === id);
            if (idx !== -1) timeLogsByProject.submissionIntervals.splice(idx, 1);
        },
        editTimeLogOfProject: (state, action: PayloadAction<TimeLogToEdit>) => {
            const editedStartMonth = toMonthKey(action.payload.logStarted);
            const timeLogId = action.payload.id;
            const projectId = action.payload.projectId;
            const timeLogsForProjects = state.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject;
            const timeLogsByProject = timeLogsForProjects.find(timeLogsForProject => timeLogsForProject.projectId === projectId);
            const editedTimeLog = timeLogsByProject?.timeLogs.find(timeLog => timeLog.id === timeLogId);
            const previousStartMonth = toMonthKey(editedTimeLog?.logStarted);

            if (editedStartMonth || previousStartMonth) {
                invalidateMonthlyCache(state.value.monthlyLogsCache, [editedStartMonth || '', previousStartMonth || '']);
            } else {
                state.value.monthlyLogsCache = {};
            }

            if (editedTimeLog) {
                editedTimeLog.description = action.payload.description;
                if (action.payload.logStarted) editedTimeLog.logStarted = action.payload.logStarted;
                if (action.payload.logEnded) editedTimeLog.logEnded = action.payload.logEnded;
            }
        },
        setTimeTrackingView: (state, action: PayloadAction<TimeTrackingView>) => {
            state.value.timeTrackingView = action.payload;
        },
        addPendingTimeLog: (state, action: PayloadAction<TimeLogDto>) => {
            state.value.pendingTimeLogs.push(action.payload);
        },
        openProjectWrapper: (state, action: PayloadAction<string>) => {
            const exist = state.value.openedProjectWrappers.find(prj => prj === action.payload);
            if (!exist) state.value.openedProjectWrappers.push(action.payload);
        },
        closeProjectWrapper: (state, action: PayloadAction<string>) => {
            state.value.openedProjectWrappers = state.value.openedProjectWrappers.filter(prj => prj !== action.payload);
        },
        setActingUserSub: (state, action: PayloadAction<string | null>) => {
            state.value.actingUserSub = action.payload;
        },
    },
});

export const {
    setTimeTrackingTimestamp,
    setTimeTrackingWeek,
    setTimeLogsByProjectWithLeaveRequests,
    addTimeLogOfProjectForView,
    removeTimeLogOfProject,
    addSubmissionIntervalOfProject,
    deleteSubmissionIntervalOfProject,
    editTimeLogOfProject,
    setTimeTrackingView,
    openProjectWrapper,
    closeProjectWrapper,
    addTimeLogForProject,
    setActingUserSub,
    setMonthlyLogsCacheEntry,
} = timeTrackingSlice.actions;

export const selectTimeTrackingDate = (state: RootState) => DateTime.fromISO(state.timeTracking.value.timeTrackingTimestamp);

export const selectTimeTrackingWeek = (state: RootState) => ({
    start: DateTime.fromISO(state.timeTracking.value.timeTrackingWeekStart),
    end: DateTime.fromISO(state.timeTracking.value.timeTrackingWeenEnd),
});

export const selectTimeTrackingWeekISO = (state: RootState) => ({
    start: state.timeTracking.value.timeTrackingWeekStart,
    end: state.timeTracking.value.timeTrackingWeenEnd,
});

export const selectLeaveRequests = (state: RootState) => state.timeTracking.value.timeLogsByProjectWithLeaveRequests.leaveRequests;

export const getPendingTimeLogs = (state: RootState) => state.timeTracking.value.pendingTimeLogs;

export const getOpenedProjectWrappers = (state: RootState) => state.timeTracking.value.openedProjectWrappers;
export const selectActingUserSub = (state: RootState) => state.timeTracking.value.actingUserSub;
export const selectMonthlyLogsCache = (state: RootState) => state.timeTracking.value.monthlyLogsCache;

export const selectFilteredTimeLogsForProjects = createSelector(
    [
        (state: RootState) => state.timeTracking.value.timeLogsByProjectWithLeaveRequests.timeLogsByProject,
        (state: RootState) => state.auth.value.role.name,
    ],
    (projects, roleName) => {
        if (isNotPrivileged(roleName)) return projects.filter(project => project.projectStatus !== ProjectStatus.COMPLETED);

        return projects;
    }
);

export default timeTrackingSlice.reducer;
